import type { AnalyticsModel, ProcessView } from "./utils";
import { parseUtcDate } from "../../utils";

export type SessionProcess = {
  name: string;
  color: string;
  totalMin: number;
  runs: { pv: ProcessView; index: number }[];
};

export function sessionProcesses(model: AnalyticsModel): SessionProcess[] {
  const groups = new Map<string, SessionProcess>();
  model.processes.forEach((pv, index) => {
    if (!pv.trace.significance) return;
    const group = groups.get(pv.trace.name) ?? {
      name: pv.trace.name,
      color: pv.color,
      totalMin: 0,
      runs: [],
    };
    group.totalMin += pv.totalMin;
    group.runs.push({ pv, index });
    groups.set(group.name, group);
  });
  for (const group of groups.values()) {
    group.runs.sort((a, b) => {
      const start = (pv: ProcessView) =>
        parseUtcDate(pv.extent?.start ?? pv.trace.events[0]?.start ?? "1970-01-01").getTime();
      return start(a.pv) - start(b.pv) || a.index - b.index;
    });
  }
  return [...groups.values()].sort(
    (a, b) => b.totalMin - a.totalMin || a.name.localeCompare(b.name),
  );
}

// Count changes between chronologically ordered detected steps, not fragmented
// coverage bars or app switches (which the analysis does not expose). A rolling
// 15-minute, half-open window avoids arbitrary clock-aligned bucket boundaries.
function sessionStepChanges(model: AnalyticsModel) {
  const steps = model.processes
    .flatMap((pv, process) =>
      pv.trace.events.map((event, step) => ({
        start: parseUtcDate(event.start).getTime(),
        key: `${process}:${step}`,
      })),
    )
    .sort((a, b) => a.start - b.start || a.key.localeCompare(b.key));
  return [...new Set(steps.slice(1).map(step => step.start))];
}

export function highestActivityPeriod(model: AnalyticsModel) {
  const changes = sessionStepChanges(model);
  if (!changes.length) return null;
  let best = { start: changes[0], end: changes[0] + 900_000, count: 0 };
  let right = 0;
  changes.forEach((start, left) => {
    while (right < changes.length && changes[right] < start + 900_000) right++;
    if (right - left > best.count) best = { start, end: start + 900_000, count: right - left };
  });
  return best;
}
